/**
 * Signing in and out.
 *
 * The token lives in `localStorage` rather than memory so a refresh does not
 * sign staff out mid-task — a coordinator halfway through recording a session's
 * attendance should not lose the screen because they hit F5.
 *
 * That is a deliberate trade: a token in localStorage is readable by any script
 * that gets onto the page, where an in-memory one is not. It is the right call
 * for a small internal tool with no third-party scripts, and the wrong one for
 * a public site. Worth revisiting if this ever serves volunteers directly.
 */

import type { PasswordStatus } from '@parinaam-vms/shared';
import { apiGet, apiPost, setAuthToken } from './client';

export type { PasswordStatus };

export type UserRole = 'ADMIN' | 'TEAM' | 'VOLUNTEER';

export interface SignedInUser {
  user_id: string;
  display_name: string;
  role: UserRole;
  volunteer_id: string | null;
  /**
   * How long their password has left, computed by the API (13 Sep 2026).
   *
   * Never derived here from a date. The portal runs on the user’s own clock,
   * and a laptop three days fast would show an alert that is not true — or
   * hide one that is.
   */
  password: PasswordStatus;
}

interface LoginResponse {
  token: string;
  expires_at: string;
  user: {
    user_id: string;
    email: string;
    display_name: string;
    role: UserRole;
    volunteer_id: string | null;
  };
  password: PasswordStatus;
}

const TOKEN_KEY = 'parinaam-vms.token';

/**
 * Reading and writing storage is wrapped, because it throws rather than
 * returning null in a private window and in some embedded browsers — and a
 * portal that will not render because storage is disabled is a worse failure
 * than one that simply asks you to sign in again.
 */
export function readStoredToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeStoredToken(token: string | null): void {
  try {
    if (token === null) window.localStorage.removeItem(TOKEN_KEY);
    else window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Not fatal: the session simply lasts as long as the tab does.
  }
}

export async function login(email: string, password: string): Promise<SignedInUser> {
  const result = await apiPost<LoginResponse>('/auth/login', { email, password });

  writeStoredToken(result.token);
  setAuthToken(result.token);

  return {
    user_id: result.user.user_id,
    display_name: result.user.display_name,
    role: result.user.role,
    volunteer_id: result.user.volunteer_id,
    password: result.password,
  };
}

/**
 * Change your own password.
 *
 * The session survives it — the API spares the token that made the request and
 * revokes the rest — so there is nothing to store and nothing to sign back in
 * to. What comes back is the new expiry, which the caller folds into the
 * signed-in user so the banner disappears without a reload.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<PasswordStatus> {
  const result = await apiPost<{ password: PasswordStatus }>('/me/password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return result.password;
}

/**
 * Sign out, and mean it.
 *
 * The server call revokes the session so the token is dead everywhere, not just
 * in this browser — which is the whole reason these are opaque tokens rather
 * than JWTs. Local state is cleared either way: if the network call fails, the
 * person still expects to be signed out of the machine in front of them.
 */
export async function logout(): Promise<void> {
  try {
    await apiPost<{ signed_out: boolean }>('/auth/logout', {});
  } catch {
    // Already expired, or the API is unreachable. Neither changes what happens
    // next here.
  } finally {
    writeStoredToken(null);
    setAuthToken(null);
  }
}

/** Who the stored token belongs to, or null if it is missing or stale. */
export async function fetchCurrentUser(): Promise<SignedInUser | null> {
  const token = readStoredToken();
  if (!token) return null;

  setAuthToken(token);
  try {
    return await apiGet<SignedInUser>('/auth/me');
  } catch {
    // Expired, revoked, or the account was disabled. Clearing it here means the
    // next render asks for a sign-in rather than looping on a dead token.
    writeStoredToken(null);
    setAuthToken(null);
    return null;
  }
}
